import { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Star, Plus, X, TrendUp, TrendDown, Bell, ChartLine } from '@phosphor-icons/react';
import TradingViewWidget from '@/components/app/TradingView';

const ASSET_TVMAP = {
  EURUSD: 'FX:EURUSD', GBPUSD: 'FX:GBPUSD', USDJPY: 'FX:USDJPY', USDCHF: 'FX:USDCHF',
  USDCAD: 'FX:USDCAD', AUDUSD: 'FX:AUDUSD', NZDUSD: 'FX:NZDUSD',
  XAUUSD: 'OANDA:XAUUSD', XAGUSD: 'OANDA:XAGUSD',
  WTI: 'TVC:USOIL', BRENT: 'TVC:UKOIL',
  BTCUSD: 'BINANCE:BTCUSDT', ETHUSD: 'BINANCE:ETHUSDT',
  SOLUSD: 'BINANCE:SOLUSDT', BNBUSD: 'BINANCE:BNBUSDT',
  SPX500: 'FOREXCOM:SPXUSD', NAS100: 'FOREXCOM:NSXUSD',
  AAPL: 'NASDAQ:AAPL', TSLA: 'NASDAQ:TSLA', NVDA: 'NASDAQ:NVDA',
};

function StatCell({ label, value, className = '' }) {
  return (
    <div className="min-w-0">
      <div className="data-label truncate">{label}</div>
      <div className={`stat-num text-lg mt-0.5 ${className}`}>{value}</div>
    </div>
  );
}

export default function Trading() {
  const { user } = useAuth();
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [symbols, setSymbols] = useState({});
  const [favs, setFavs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [symbol, setSymbol] = useState('XAUUSD');
  const [quote, setQuote] = useState({});
  const [form, setForm] = useState({ order_type: 'MARKET_BUY', volume: 0.1, price: '', stop_loss: '', take_profit: '' });
  const [alertForm, setAlertForm] = useState({ condition: 'ABOVE', price: '' });

  const loadAll = async () => {
    try {
      const [a, p, pd, h, s, f, al] = await Promise.all([
        api.get('/trading/account'), api.get('/trading/positions'),
        api.get('/trading/pending'), api.get('/trading/history?limit=50'),
        api.get('/trading/symbols'), api.get('/trading/favorites'), api.get('/trading/alerts'),
      ]);
      setAccount(a.data); setPositions(p.data); setPending(pd.data);
      setHistory(h.data); setSymbols(s.data); setFavs(f.data); setAlerts(al.data);
    } catch (err) { /* ignore */ }
  };
  const loadQuote = async (s) => {
    try { const { data } = await api.get(`/trading/quote/${s}`); setQuote(data); } catch { setQuote({}); }
  };

  useEffect(() => { loadAll(); const t = setInterval(loadAll, 15000); return () => clearInterval(t); }, []);
  useEffect(() => { loadQuote(symbol); const t = setInterval(() => loadQuote(symbol), 8000); return () => clearInterval(t); }, [symbol]);

  const isPending = form.order_type.includes('LIMIT') || form.order_type.includes('STOP') && !form.order_type.startsWith('MARKET');
  const isMarket = form.order_type.startsWith('MARKET');

  const submitOrder = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        symbol, order_type: form.order_type,
        volume: parseFloat(form.volume),
        price: !isMarket && form.price ? parseFloat(form.price) : null,
        stop_loss: form.stop_loss ? parseFloat(form.stop_loss) : null,
        take_profit: form.take_profit ? parseFloat(form.take_profit) : null,
      };
      await api.post('/trading/orders', payload);
      toast.success(isMarket ? 'Order executed' : 'Pending order placed');
      setForm({ ...form, price: '', stop_loss: '', take_profit: '' });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Order failed'); }
  };

  const closePos = async (id) => {
    if (!window.confirm('Close this position?')) return;
    try { const { data } = await api.post(`/trading/positions/${id}/close`); toast.success(`Closed · P&L $${data.profit}`); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const partialClose = async (id, vol) => {
    const v = prompt('Volume to close (lots):', (vol / 2).toFixed(2));
    if (!v) return;
    try { const { data } = await api.post(`/trading/positions/${id}/partial-close`, { volume: parseFloat(v) }); toast.success(`Partial close · P&L $${data.profit}`); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const modifyPos = async (id) => {
    const sl = prompt('New Stop Loss (blank to skip):');
    const tp = prompt('New Take Profit (blank to skip):');
    if (!sl && !tp) return;
    try {
      await api.patch(`/trading/positions/${id}`, {
        stop_loss: sl ? parseFloat(sl) : null,
        take_profit: tp ? parseFloat(tp) : null,
      });
      toast.success('Modified'); loadAll();
    } catch (err) { toast.error('Failed'); }
  };
  const cancelPending = async (id) => {
    if (!window.confirm('Cancel this pending order?')) return;
    try { await api.delete(`/trading/pending/${id}`); toast.success('Cancelled'); loadAll(); }
    catch { toast.error('Failed'); }
  };

  const toggleFav = async (sym) => {
    const next = favs.includes(sym) ? favs.filter(s => s !== sym) : [...favs, sym];
    setFavs(next);
    await api.post('/trading/favorites', { symbols: next });
  };
  const addAlert = async (e) => {
    e.preventDefault();
    try { await api.post('/trading/alerts', { symbol, condition: alertForm.condition, price: parseFloat(alertForm.price) });
      toast.success('Alert added'); setAlertForm({...alertForm, price:''}); loadAll();
    } catch { toast.error('Failed'); }
  };
  const delAlert = async (id) => { await api.delete(`/trading/alerts/${id}`); loadAll(); };

  const allSymbols = useMemo(() => Object.values(symbols).flat(), [symbols]);

  if (!account) return <div className="text-muted-foreground p-8">Loading trading terminal…</div>;

  return (
    <div className="space-y-4" data-testid="trading-page">
      {/* Account summary strip */}
      <div className="glass rounded-xl p-4 grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-4 items-center">
        <div className="col-span-3 md:col-span-2">
          <div className="data-label">Account</div>
          <div className="font-heading font-bold">{account.account_number}</div>
          <div className="text-xs text-muted-foreground">{account.account_type} · {account.currency} · Leverage 1:{account.leverage}</div>
        </div>
        <StatCell label="Balance" value={`$${account.balance.toFixed(2)}`} />
        <StatCell label="Equity" value={`$${account.equity.toFixed(2)}`} className="text-saffron" />
        <StatCell label="Floating P/L" value={`$${account.floating_pl.toFixed(2)}`} className={account.floating_pl >= 0 ? 'text-green-400' : 'text-red-400'} />
        <StatCell label="Margin" value={`$${account.margin_used.toFixed(2)}`} />
        <StatCell label="Free Margin" value={`$${account.free_margin.toFixed(2)}`} />
        <StatCell label="Margin Level" value={`${account.margin_level}%`} />
        <StatCell label="Open" value={account.open_count} />
      </div>

      <div className="grid lg:grid-cols-[240px_1fr_320px] gap-4">
        {/* Watchlist / Symbols */}
        <aside className="glass rounded-xl p-3 flex flex-col max-h-[65vh] overflow-hidden">
          <div className="data-label mb-2 px-2">Watchlist</div>
          <div className="flex-1 overflow-y-auto pr-1">
            {Object.entries(symbols).map(([grp, syms]) => (
              <div key={grp} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 mb-1">{grp}</div>
                {syms.map(sy => (
                  <div key={sy} onClick={() => setSymbol(sy)} data-testid={`sym-${sy}`}
                    className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer text-xs font-mono ${symbol === sy ? 'bg-saffron/15 text-saffron' : 'hover:bg-white/5'}`}>
                    <span>{sy}</span>
                    <button onClick={(e) => { e.stopPropagation(); toggleFav(sy); }} className="text-muted-foreground hover:text-saffron">
                      <Star size={12} weight={favs.includes(sy) ? 'fill' : 'regular'} className={favs.includes(sy) ? 'text-saffron' : ''} />
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Chart */}
        <section className="glass rounded-xl p-3 flex flex-col">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div>
                <div className="font-heading font-bold text-lg font-mono">{symbol}</div>
                <div className="text-xs text-muted-foreground">Live · {quote.class || ''}</div>
              </div>
              {quote.price && <div className="font-mono text-2xl font-bold text-saffron" data-testid="live-price">{quote.price.toFixed(quote.class === 'FX' ? 5 : 2)}</div>}
            </div>
          </div>
          <div className="flex-1 min-h-[420px]">
            <TradingViewWidget symbol={ASSET_TVMAP[symbol] || `FX:${symbol}`} height={480} interval="60" />
          </div>
        </section>

        {/* Order Entry */}
        <aside className="glass rounded-xl p-4 space-y-3">
          <div className="font-heading font-bold">Order Entry</div>
          <form onSubmit={submitOrder} className="space-y-3">
            <div>
              <Label className="data-label">Order Type</Label>
              <select value={form.order_type} onChange={e => setForm({...form, order_type: e.target.value})} data-testid="order-type"
                className="mt-1 w-full h-10 rounded-md bg-surface-base border border-white/10 px-3 text-sm">
                <optgroup label="Market">
                  <option value="MARKET_BUY">Market Buy</option>
                  <option value="MARKET_SELL">Market Sell</option>
                </optgroup>
                <optgroup label="Pending">
                  <option value="BUY_LIMIT">Buy Limit</option>
                  <option value="SELL_LIMIT">Sell Limit</option>
                  <option value="BUY_STOP">Buy Stop</option>
                  <option value="SELL_STOP">Sell Stop</option>
                </optgroup>
              </select>
            </div>
            <div>
              <Label className="data-label">Volume (lots)</Label>
              <Input type="number" step="0.01" min="0.01" required value={form.volume} onChange={e => setForm({...form, volume: e.target.value})} className="mt-1 bg-surface-base border-white/10" data-testid="order-volume" />
            </div>
            {!isMarket && (
              <div>
                <Label className="data-label">Trigger Price</Label>
                <Input type="number" step="any" required value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="mt-1 bg-surface-base border-white/10" data-testid="order-price" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="data-label">Stop Loss</Label>
                <Input type="number" step="any" value={form.stop_loss} onChange={e => setForm({...form, stop_loss: e.target.value})} className="mt-1 bg-surface-base border-white/10" data-testid="order-sl" /></div>
              <div><Label className="data-label">Take Profit</Label>
                <Input type="number" step="any" value={form.take_profit} onChange={e => setForm({...form, take_profit: e.target.value})} className="mt-1 bg-surface-base border-white/10" data-testid="order-tp" /></div>
            </div>
            <Button type="submit" disabled={!account.trading_enabled || account.frozen} data-testid="submit-order"
              className={`w-full h-11 font-bold text-white ${form.order_type.includes('BUY') ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}>
              {form.order_type.replace('_',' ')}
            </Button>
            {(!account.trading_enabled || account.frozen) && <div className="text-xs text-red-400 text-center">Trading disabled by admin</div>}
          </form>

          <div className="pt-3 border-t border-white/5">
            <div className="font-heading font-semibold text-sm mb-2 flex items-center gap-2"><Bell size={14} className="text-saffron" /> Price Alert</div>
            <form onSubmit={addAlert} className="flex gap-2">
              <select value={alertForm.condition} onChange={e => setAlertForm({...alertForm, condition: e.target.value})} className="h-9 rounded bg-surface-base border border-white/10 px-2 text-xs">
                <option>ABOVE</option><option>BELOW</option>
              </select>
              <Input type="number" step="any" required placeholder="Price" value={alertForm.price} onChange={e => setAlertForm({...alertForm, price: e.target.value})} className="bg-surface-base border-white/10 h-9 text-xs" />
              <Button type="submit" size="sm" className="bg-saffron hover:bg-saffron-hover text-white h-9 px-3"><Plus size={14} /></Button>
            </form>
            <div className="space-y-1 mt-2 max-h-24 overflow-y-auto">
              {alerts.filter(a => a.symbol === symbol).map(a => (
                <div key={a.id} className="flex justify-between items-center text-xs p-1.5 rounded bg-surface-base">
                  <span>{a.condition} {a.price}</span>
                  <button onClick={() => delAlert(a.id)} className="text-red-400"><X size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Positions / Pending / History */}
      <Tabs defaultValue="positions" className="w-full">
        <TabsList className="glass border border-white/5">
          <TabsTrigger value="positions" data-testid="tab-positions">Open Positions ({positions.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="positions" className="mt-3">
          <div className="glass rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/5">
                <tr>{['Ticket','Symbol','Dir','Vol','Open','Current','SL','TP','P/L','Time','Actions'].map(h => <th key={h} className="p-3 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {positions.length === 0 && <tr><td colSpan="11" className="p-6 text-center text-muted-foreground">No open positions.</td></tr>}
                {positions.map(p => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 font-mono">{p.ticket}</td>
                    <td className="p-3 font-mono font-semibold">{p.symbol}</td>
                    <td className={`p-3 font-bold ${p.direction === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>{p.direction}</td>
                    <td className="p-3 font-mono">{p.volume}</td>
                    <td className="p-3 font-mono">{p.open_price}</td>
                    <td className="p-3 font-mono">{p.current_price?.toFixed?.(4) ?? '—'}</td>
                    <td className="p-3 font-mono">{p.stop_loss || '—'}</td>
                    <td className="p-3 font-mono">{p.take_profit || '—'}</td>
                    <td className={`p-3 font-mono font-bold ${p.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>${p.profit}</td>
                    <td className="p-3 text-muted-foreground">{new Date(p.open_time).toLocaleString()}</td>
                    <td className="p-3 flex gap-1 flex-wrap">
                      <button onClick={() => modifyPos(p.id)} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">Modify</button>
                      <button onClick={() => partialClose(p.id, p.volume)} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">Partial</button>
                      <button onClick={() => closePos(p.id)} data-testid={`close-${p.id}`} className="text-xs px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400">Close</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-3">
          <div className="glass rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/5">
                <tr>{['Ticket','Symbol','Type','Vol','Trigger','Current','SL','TP','Created','Actions'].map(h => <th key={h} className="p-3 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {pending.length === 0 && <tr><td colSpan="10" className="p-6 text-center text-muted-foreground">No pending orders.</td></tr>}
                {pending.map(o => (
                  <tr key={o.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 font-mono">{o.ticket}</td>
                    <td className="p-3 font-mono">{o.symbol}</td>
                    <td className="p-3">{o.order_type.replace('_',' ')}</td>
                    <td className="p-3 font-mono">{o.volume}</td>
                    <td className="p-3 font-mono">{o.price}</td>
                    <td className="p-3 font-mono">{o.current_price?.toFixed?.(4) ?? '—'}</td>
                    <td className="p-3 font-mono">{o.stop_loss || '—'}</td>
                    <td className="p-3 font-mono">{o.take_profit || '—'}</td>
                    <td className="p-3 text-muted-foreground">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-3"><button onClick={() => cancelPending(o.id)} className="text-xs px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400">Cancel</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-3">
          <div className="glass rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/5">
                <tr>{['Time','Type','Symbol','Dir','Vol','Open','Close','P/L','Commission','Swap'].map(h => <th key={h} className="p-3 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {history.length === 0 && <tr><td colSpan="10" className="p-6 text-center text-muted-foreground">No history yet.</td></tr>}
                {history.map(h => (
                  <tr key={h.id} className="border-b border-white/5">
                    <td className="p-3 text-muted-foreground">{new Date(h.close_time).toLocaleString()}</td>
                    <td className="p-3">{h.type}</td>
                    <td className="p-3 font-mono">{h.symbol}</td>
                    <td className={`p-3 font-bold ${h.direction === 'BUY' ? 'text-green-400' : h.direction === 'SELL' ? 'text-red-400' : ''}`}>{h.direction}</td>
                    <td className="p-3 font-mono">{h.volume}</td>
                    <td className="p-3 font-mono">{h.open_price || '—'}</td>
                    <td className="p-3 font-mono">{h.close_price || '—'}</td>
                    <td className={`p-3 font-mono font-bold ${h.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>${h.profit}</td>
                    <td className="p-3 font-mono">${h.commission}</td>
                    <td className="p-3 font-mono">${h.swap}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
